/**
 * @fileoverview Standalone recording-consent reference page.
 *
 * Reachable only at /recording-consent and deliberately linked from NOWHERE in
 * the app (no navbar/footer entry) — it exists to be shared as a direct link on
 * external sites (e.g. a partner school's page). It renders the generic,
 * tokenless recording-consent agreement + COPPA notice served by
 * GET /api/consent/agreement — the canonical copy from consent_text.py with no
 * student/instructor names — and is marked noindex so it stays out of search.
 *
 * Visual language matches the other public legal pages (/privacy, /terms): the
 * dark public-marketing aesthetic, not the Studio Quiet instructor portal. The
 * few raw-palette utilities below are a deliberate, reviewed exception to the
 * brand guard (they mirror PrivacyPage) — hence the brand-lint-ignore markers.
 */

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import PublicLayout from "@/components/PublicLayout";
import { supportEmail } from "@/lib/companyBrand";

// This page carried its own hardcoded address while companyBrand's helpers
// were still hostname-conditional. With the lab401.ai domain retired they
// resolve to the same address unconditionally, so it routes through the
// shared helper like every other contact link. See lib/companyBrand.js.
const SUPPORT_EMAIL = supportEmail();

// Public-legal-page section — same dark aesthetic as /privacy and /terms.
const Section = ({ title, children }) => (
  <div className="py-8 border-b border-white/5 last:border-0">{/* brand-lint-ignore public legal-page aesthetic (matches /privacy) */}
    <h2 className="text-base font-medium mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
      {title}
    </h2>
    <div className="text-sm text-zinc-300 leading-7 whitespace-pre-line">{children}</div>{/* brand-lint-ignore public legal-page aesthetic (matches /privacy) */}
  </div>
);

const RecordingConsentPage = () => {
  const [state, setState] = useState({ loading: true, data: null, error: false });

  // Unlisted: keep it out of search indexes so it's only reachable via the link.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    axios
      .get(`${API}/consent/agreement`, { signal: controller.signal })
      .then((res) => setState({ loading: false, data: res.data, error: false }))
      .catch((err) => {
        if (axios.isCancel?.(err)) return;
        setState({ loading: false, data: null, error: true });
      });
    return () => controller.abort();
  }, []);

  const { loading, data, error } = state;

  return (
    <PublicLayout>
      <main className="relative z-10 px-4 md:px-8 pt-12 md:pt-20 pb-20 md:pb-32 max-w-2xl mx-auto safe-area-padding">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          {/* Header */}
          <div className="mb-10">
            <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-4">Consent</p>{/* brand-lint-ignore public legal-page aesthetic */}
            <h1
              className="text-3xl md:text-4xl font-light tracking-tight mb-5"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Recording consent
            </h1>
            <p className="text-sm text-zinc-400 leading-7">{/* brand-lint-ignore public legal-page aesthetic */}
              Music teachers use Hans to record lessons and turn them into written
              summaries with practice notes. Before any recording begins, the student — or
              a parent, for a minor — is shown the agreement below and must explicitly
              agree. This is that agreement in full; the teacher&rsquo;s and student&rsquo;s
              names are filled in for each individual request.
            </p>
          </div>

          {loading && (
            <div className="flex items-center gap-3 text-zinc-500 py-12">{/* brand-lint-ignore public legal-page aesthetic */}
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Loading the agreement&hellip;</span>
            </div>
          )}

          {error && (
            <div className="text-sm text-zinc-400 leading-7 py-12">{/* brand-lint-ignore public legal-page aesthetic */}
              We couldn&rsquo;t load the agreement right now. Please try again, or contact us
              at{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#f4cf08] hover:underline">
                {SUPPORT_EMAIL}
              </a>
              .
            </div>
          )}

          {data && (
            <>
              <Section title="Recording consent agreement">{data.consent_text}</Section>
              {data.coppa_direct_notice && (
                <Section title="Additional notice for children under 13 (COPPA)">
                  {data.coppa_direct_notice}
                </Section>
              )}
              <div className="pt-8">
                <p className="text-xs text-zinc-600">{/* brand-lint-ignore public legal-page aesthetic */}
                  Consent text version {data.consent_text_version}. Questions? Contact{" "}
                  <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#f4cf08] hover:underline">
                    {SUPPORT_EMAIL}
                  </a>
                  .
                </p>
              </div>
            </>
          )}
        </motion.div>
      </main>
    </PublicLayout>
  );
};

export default RecordingConsentPage;
